import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Payments as PaymentsIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  LocalOffer as DiscountIcon,
  Visibility as ViewIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import AdminLayout from './components/AdminLayout';
import { 
  getAllPayments, 
  Payment 
} from '../../services/paymentService';
import { DiscountCode } from '../../services/discountCodeService';
import DiscountCodeList from '../../components/admin/payments/DiscountCodeList';
import DiscountCodeFormDialog from '../../components/admin/payments/DiscountCodeFormDialog';
import PaymentDetailsDialog from '../../components/admin/payments/PaymentDetailsDialog';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`payment-tabpanel-${index}`}
      aria-labelledby={`payment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

// Payment status chip component
const PaymentStatusChip = ({ status }: { status: string }) => {
  const getChipProps = () => {
    switch (status) {
      case 'created':
        return { label: 'Created', color: 'default' as const };
      case 'approved':
        return { label: 'Approved', color: 'primary' as const };
      case 'captured':
        return { label: 'Completed', color: 'success' as const };
      case 'pending':
        return { label: 'Pending', color: 'warning' as const };
      case 'denied':
      case 'expired':
        return { label: status === 'denied' ? 'Denied' : 'Expired', color: 'error' as const };
      case 'refunded':
        return { label: 'Refunded', color: 'secondary' as const };
      default:
        return { label: status, color: 'default' as const };
    }
  };

  const { label, color } = getChipProps();
  return <Chip size="small" label={label} color={color} />;
};

interface PaymentManagementPageProps {
  initialTab?: number;
  openDiscountDialog?: boolean;
}

const PaymentManagementPage: React.FC<PaymentManagementPageProps> = ({
  initialTab: _initialTab = 0,
  openDiscountDialog = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use URL to determine active tab
  // Get the tab index based on URL
  const getTabFromUrl = useCallback(() => {
    return location.pathname.includes('/discount-codes') ? 1 : 0;
  }, [location.pathname]);

  // State for tab management - driven by URL
  const [tabValue, setTabValue] = useState(getTabFromUrl());
  
  // Sync tab value with URL when location changes
  useEffect(() => {
    const tabFromUrl = getTabFromUrl();
    if (tabValue !== tabFromUrl) {
      setTabValue(tabFromUrl);
    }
  }, [location.pathname, getTabFromUrl, tabValue]);
  
  // State for payments data
  const [payments, setPayments] = useState<Payment[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for filtering
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | 'all'>('all');
  
  // State for discount code dialog
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [selectedDiscountCode, setSelectedDiscountCode] = useState<DiscountCode | null>(null);
  
  // State for payment details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  
  // State for success messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Payment statistics
  const [stats, setStats] = useState({
    totalPayments: 0,
    totalRevenue: 0,
    averagePayment: 0,
    pendingPayments: 0
  });

  // Fetch all payments
  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const allPayments = await getAllPayments();
      setPayments(allPayments);
      
      // Calculate payment statistics
      const totalRevenue = allPayments.reduce((sum, payment) => 
        payment.paymentStatus === 'captured' ? sum + payment.amount : sum, 0);
        
      const capturedPayments = allPayments.filter(p => p.paymentStatus === 'captured');
      const averagePayment = capturedPayments.length > 0 
        ? totalRevenue / capturedPayments.length 
        : 0;
        
      const pendingPayments = allPayments.filter(p => 
        ['created', 'approved', 'pending'].includes(p.paymentStatus)).length;
        
      setStats({
        totalPayments: allPayments.length,
        totalRevenue,
        averagePayment,
        pendingPayments
      });
    } catch (error) {
      console.error('Error fetching payments:', error);
      setError('Failed to load payment data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters to payments
  const applyFilters = useCallback(() => {
    let filtered = [...payments];
    
    // Apply search query filter
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(payment => 
        payment.paypalOrderId?.toLowerCase().includes(lowerQuery) ||
        payment.id.toString().includes(lowerQuery)
      );
    }
    
    // Apply date range filter
    if (startDate) {
      filtered = filtered.filter(payment => 
        dayjs(payment.transactionDate).isAfter(startDate) || 
        dayjs(payment.transactionDate).isSame(startDate, 'day')
      );
    }
    
    if (endDate) {
      filtered = filtered.filter(payment => 
        dayjs(payment.transactionDate).isBefore(endDate) || 
        dayjs(payment.transactionDate).isSame(endDate, 'day')
      );
    }
    
    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(payment => payment.paymentStatus === selectedStatus);
    }
    
    setFilteredPayments(filtered);
  }, [searchQuery, startDate, endDate, selectedStatus, payments]);

  // Fetch payments when component mounts
  useEffect(() => {
    fetchPayments();
  }, []);

  // Apply filters whenever filter conditions change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);
  
  // Handle openDiscountDialog prop
  useEffect(() => {
    // If we need to open the discount dialog and we're on the discount tab
    if (openDiscountDialog && tabValue === 1) {
      // Short timeout to ensure component is fully rendered
      const timer = setTimeout(() => {
        handleOpenDiscountDialog();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [openDiscountDialog, tabValue]);

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // Only update URL - the tab state will update via the location change effect
    const newPath = newValue === 0 
      ? '/admin/payments' 
      : '/admin/payments/discount-codes';
    
    // Use replace to avoid creating unnecessary history entries
    navigate(newPath, { replace: true });
  };

  // Handle opening the payment details dialog
  const handleViewPaymentDetails = (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailsDialogOpen(true);
  };

  // Handle discount code dialog
  const handleOpenDiscountDialog = (discountCode: DiscountCode | null = null) => {
    setSelectedDiscountCode(discountCode);
    setDiscountDialogOpen(true);
  };

  const handleCloseDiscountDialog = (success = false, isEdit = false) => {
    setDiscountDialogOpen(false);
    
    // Set success message if operation was successful
    if (success) {
      const action = isEdit ? 'updated' : 'created';
      setSuccessMessage(`Discount code successfully ${action}!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    }
    
    setSelectedDiscountCode(null);
    
    // Reload discount codes without changing tabs or reloading the page
    if (tabValue === 1) {
      fetchPayments();
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <AdminLayout>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: '#e3f2fd' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" component="div" fontWeight="bold">
                {stats.totalPayments}
              </Typography>
              <Box sx={{ color: '#1976d2' }}>
                <PaymentsIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Total Payments
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: '#e8f5e9' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" component="div" fontWeight="bold">
                {formatCurrency(stats.totalRevenue)}
              </Typography>
              <Box sx={{ color: '#388e3c' }}>
                <ReceiptIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Total Revenue
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: '#fff8e1' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" component="div" fontWeight="bold">
                {formatCurrency(stats.averagePayment)}
              </Typography>
              <Box sx={{ color: '#ffa000' }}>
                <PaymentsIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Average Payment
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: '#ffebee' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h5" component="div" fontWeight="bold">
                {stats.pendingPayments}
              </Typography>
              <Box sx={{ color: '#d32f2f' }}>
                <PaymentsIcon sx={{ fontSize: 40 }} />
              </Box>
            </Box>
            <Typography variant="body1" color="text.secondary">
              Pending Payments
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs Navigation */}
      <Paper elevation={2} sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          centered
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label="Payments" 
            icon={<PaymentsIcon />} 
            iconPosition="start"
            id="payment-tab-0"
            aria-controls="payment-tabpanel-0"
          />
          <Tab 
            label="Discount Codes" 
            icon={<DiscountIcon />} 
            iconPosition="start"
            id="payment-tab-1"
            aria-controls="payment-tabpanel-1"
          />
        </Tabs>
      </Paper>

      {/* Payments Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Filters */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                placeholder="Order ID or payment ID"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker 
                label="From Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker 
                label="To Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={5} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setSearchQuery('');
                  setStartDate(null);
                  setEndDate(null);
                  setSelectedStatus('all');
                  fetchPayments();
                }}
              >
                Reset Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
        
        {/* Payment List */}
        <Paper elevation={2}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Discount Code</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography variant="body1" sx={{ py: 3 }}>
                            No payments found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPayments
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((payment) => (
                          <TableRow key={payment.id} hover>
                            <TableCell>{payment.id}</TableCell>
                            <TableCell>{payment.paypalOrderId || '-'}</TableCell>
                            <TableCell>
                              {new Date(payment.transactionDate).toLocaleDateString()}
                            </TableCell>
                            <TableCell>{formatCurrency(payment.amount)}</TableCell>
                            <TableCell>
                              <PaymentStatusChip status={payment.paymentStatus} />
                            </TableCell>
                            <TableCell>
                              {payment.discountCodeId ? (
                                <Chip 
                                  size="small" 
                                  label="Applied" 
                                  color="primary" 
                                  variant="outlined"
                                  icon={<DiscountIcon fontSize="small" />}
                                />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="View Details">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleViewPaymentDetails(payment)}
                                >
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredPayments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      </TabPanel>

      {/* Discount Codes Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Discount Codes
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDiscountDialog()}
          >
            Create Discount Code
          </Button>
        </Box>
        
        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}
        
        <DiscountCodeList 
          onEdit={handleOpenDiscountDialog}
          key={`discount-codes-${successMessage ? 'updated' : 'regular'}`} // Force re-render when success message changes
        />
      </TabPanel>

      {/* Discount Code Dialog */}
      {discountDialogOpen && (
        <DiscountCodeFormDialog
          open={discountDialogOpen}
          onClose={handleCloseDiscountDialog}
          discountCode={selectedDiscountCode}
        />
      )}

      {/* Payment Details Dialog */}
      {detailsDialogOpen && selectedPayment && (
        <PaymentDetailsDialog
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          paymentId={selectedPayment.id}
        />
      )}
    </AdminLayout>
  );
};

export default PaymentManagementPage;